import {
    cssVar,
    Flex,
    Icon,
    VStack,
    Box,
    Input,
    Text,
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import React, { useRef } from 'react';
import { v4 } from 'uuid';
import { ToggleGroup, ToggleOption } from '../../common/components/toggle-group';
import { Tooltip } from '../../common/components/tooltip';
import { LocationTableV1 } from './location-table/location-table-v1';
import { MapboxLocationMap } from './location-map/mapbox-location-map';
import { EsriLocationMap } from './location-map/esri-location-map';
import { useLocationSelectState } from './use-location-select-state';
import { LocationSelectProps } from '../types/location-select';
import {
    getLocationSummarySecondLineText,
    getLocationSummaryThirdLineText,
} from '../helpers/location-summary';
import { getOneSizeSmaller } from '../../common/helpers/utils';

const toggleOptions: ToggleOption[] = [
    { id: 'LIST', label: 'LIST' },
    { id: 'MAP', label: 'MAP' },
];

export const LocationSelectV1 = observer(
    function LocationSelect(props: LocationSelectProps, ref): React.ReactElement {
        const {
            autoFocus,
            isDisabled,
            moveFocusNext,
            onBlur,
            onChange,
            placeholder,
            popoverRef,
            value,
            hideLocationSummaryAdditionalLines,
            ...inputProps
        } = props;

        const {
            closePopover,
            composedRef,
            currentMode,
            inputRef,
            isMapDisabled,
            isPopoverOpen,
            locationDisplayProps,
            onInputChange,
            onToggle,
            openPopover,
            query,
            resultCountWithoutForcedLocations,
            useMapboxMapForLocationInputs,
            wrapperRef,
        } = useLocationSelectState({ ...props, ref });

        const size = locationDisplayProps.size;
        const isLgSize = size === 'lg';
        const smallerSize = getOneSizeSmaller(size);
        const instanceIdRef = useRef(v4());
        const instanceId = instanceIdRef.current;
        const ariaControlsId = `location-select-v1-results-listbox-${instanceId}`;
        const optionIdPrefix = `location-select-option-${instanceId}`;
        const highlightedItem =
            locationDisplayProps.store.highlightedLocationIndex !== null
                ? locationDisplayProps.store.renderedLocationData.renderedList[
                      locationDisplayProps.store.highlightedLocationIndex
                  ]
                : undefined;
        const activeDescendantId =
            isPopoverOpen &&
            currentMode === 'LIST' &&
            highlightedItem !== undefined
                ? `${optionIdPrefix}-${highlightedItem.id}`
                : undefined;

        const locationSummarySecondLineText = !hideLocationSummaryAdditionalLines
            ? getLocationSummarySecondLineText(value)
            : '';
        const locationSummaryThirdLineText = !hideLocationSummaryAdditionalLines
            ? getLocationSummaryThirdLineText(value)
            : '';

        return (
            <Box ref={wrapperRef} w="100%">
                <Popover isOpen={isPopoverOpen} hasArrow={false}>
                    <VStack spacing={0} alignItems="flex-start" className="location-input-wrapper">
                        <PopoverAnchor asChild>
                            <Input
                                aria-label={inputProps['aria-label']}
                                role="combobox"
                                aria-autocomplete="list"
                                aria-expanded={isPopoverOpen && currentMode === 'LIST'}
                                aria-controls={
                                    isPopoverOpen && currentMode === 'LIST'
                                        ? ariaControlsId
                                        : undefined
                                }
                                aria-activedescendant={activeDescendantId}
                                // @ts-expect-error arc complains about the ref type but it works
                                ref={composedRef}
                                isDisabled={isDisabled}
                                autoFocus={autoFocus}
                                value={query}
                                data-testid="location-select-input"
                                onClick={() => {
                                    if (!isPopoverOpen) {
                                        openPopover();
                                    }
                                }}
                                onChange={onInputChange}
                                onBlur={onBlur}
                                rightAddon={
                                    value?.areCoordinatesVerified ? (
                                        <Tooltip overlay={<Text variant="caption">VERIFIED</Text>}>
                                            <Icon
                                                icon="VerifiedLocation"
                                                color="positive.default"
                                                data-testid="location-select-verified"
                                                style={{ zIndex: 1 }}
                                            />
                                        </Tooltip>
                                    ) : value?.source === 'FORCED_LOCATION' &&
                                      //@ts-expect-error
                                      value?.isForcedSubpremiseResult !== undefined ? (
                                        <Text>FORCED</Text>
                                    ) : undefined
                                }
                                placeholder={placeholder}
                            />
                        </PopoverAnchor>
                        {locationSummarySecondLineText ? (
                            <Text fontSize={smallerSize}>{locationSummarySecondLineText}</Text>
                        ) : undefined}
                        {locationSummaryThirdLineText ? (
                            <Text
                                fontSize={smallerSize}
                                style={{ color: cssVar('arc.colors.text.tertiary') }}
                            >
                                {locationSummaryThirdLineText}
                            </Text>
                        ) : undefined}
                    </VStack>
                    <PopoverContent
                        align="start"
                        side="bottom"
                        hasPadding={false}
                        onInteractOutside={(e) => {
                            if (e.target !== inputRef.current) {
                                closePopover();
                                // @ts-expect-error there's a typeerror about the type of event
                                // here but it works
                                onBlur?.(e);
                            }
                        }}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        ref={popoverRef}
                    >
                        <Box
                            style={{
                                width: isLgSize ? '700px' : '600px',
                                textTransform: 'uppercase',
                            }}
                        >
                            <Flex
                                paddingY={3}
                                pl={3}
                                pr={3}
                                justifyContent="space-between"
                                alignContent="center"
                            >
                                <Text fontSize={smallerSize} fontWeight="bold">
                                    {resultCountWithoutForcedLocations} results for {`"${query}"`}
                                </Text>
                                <ToggleGroup
                                    disabledOptions={isMapDisabled ? ['MAP'] : []}
                                    onItemChanged={onToggle}
                                    options={toggleOptions}
                                    value={toggleOptions.find((t) => t.id === currentMode)}
                                />
                            </Flex>
                            {currentMode === 'LIST' ? (
                                <LocationTableV1
                                    {...locationDisplayProps}
                                    listBoxId={ariaControlsId}
                                    optionIdPrefix={optionIdPrefix}
                                />
                            ) : useMapboxMapForLocationInputs ? (
                                <MapboxLocationMap {...locationDisplayProps} />
                            ) : (
                                <EsriLocationMap {...locationDisplayProps} />
                            )}
                        </Box>
                    </PopoverContent>
                </Popover>
            </Box>
        );
    },
    { forwardRef: true }
);
